import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { savingsService } from '../../services/savingsService';
import { formatCurrency, formatDate, getStatusColor } from '../../utils/formatters';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  ChartBarIcon,
  BanknotesIcon,
  CurrencyDollarIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';

const SavingsAccountList = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [page, setPage] = useState(1);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [transactionAmount, setTransactionAmount] = useState('');
  const [withdrawReason, setWithdrawReason] = useState('');
  const [whatsappLink, setWhatsappLink] = useState(null);
  const [showSuccessBox, setShowSuccessBox] = useState(false);
  const [lastDepositedAmount, setLastDepositedAmount] = useState(0);

  // Fetch savings accounts with filters
  const { data, isLoading, error, refetch } = useQuery(
    ['savings-accounts', filterStatus, filterType, page, searchTerm],
    () => savingsService.getSavingsAccounts({
      status: filterStatus !== 'all' ? filterStatus : undefined,
      scheme_type: filterType !== 'all' ? filterType : undefined,
      page,
      limit: 20,
      search: searchTerm || undefined
    }),
    { keepPreviousData: true }
  );

  // Deposit mutation
  const depositMutation = useMutation(
    ({ accountId, amount, paymentMethod }) =>
      savingsService.deposit(accountId, { amount, payment_method: paymentMethod }),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries('savings-accounts');

        // Get the whatsapp_link from the response data
        const link = response?.data?.whatsapp_link || response?.whatsapp_link;

        if (link) {
          setWhatsappLink(link);
          setLastDepositedAmount(parseFloat(transactionAmount));
          setShowSuccessBox(true);
          toast.success('Deposit successful! Receipt ready to share.');
        } else {
          toast.success('Deposit successful!');
        }

        setShowDepositModal(false);
        setTransactionAmount('');
        setSelectedAccount(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Deposit failed');
      }
    }
  );

  // Withdraw mutation
  const withdrawMutation = useMutation(
    ({ accountId, amount, reason, paymentMethod }) =>
      savingsService.withdraw(accountId, { amount, reason, payment_method: paymentMethod }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('savings-accounts');
        toast.success('Withdrawal successful!');
        setShowWithdrawModal(false);
        setTransactionAmount('');
        setWithdrawReason('');
        setSelectedAccount(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Withdrawal failed');
      }
    }
  );

  const accounts = data?.data || [];
  const pagination = data?.pagination || {};

  // Statistics
  const stats = {
    total: accounts.reduce((sum, acc) => sum + (acc.current_balance || 0), 0),
    active: accounts.filter(a => a.status === 'active').length,
    matured: accounts.filter(a => a.current_status === 'matured').length,
    dps: accounts.filter(a => a.scheme_type === 'dps').length,
    fdr: accounts.filter(a => a.scheme_type === 'fdr').length,
    daily: accounts.filter(a => a.scheme_type === 'daily_savings').length
  };

  const handleDeposit = (account) => {
    setSelectedAccount(account);
    setShowDepositModal(true);
  };

  const handleWithdraw = (account) => {
    setSelectedAccount(account);
    setShowWithdrawModal(true);
  };

  const handleViewDetails = (accountId) => {
    // Navigate to account details page
    window.location.href = `/savings/${accountId}/transactions`;
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Error loading savings accounts. Please try again later.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Savings Accounts</h1>
          <p className="text-gray-600 mt-1">Manage all savings and deposit accounts</p>
        </div>
        <Link
          to="/savings/new"
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Open New Account
        </Link>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow p-4 text-white">
          <BanknotesIcon className="h-8 w-8 mb-2 opacity-90" />
          <div className="text-sm opacity-90">Total Balance</div>
          <div className="text-xl font-bold">{formatCurrency(stats.total)}</div>
        </div>
        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg shadow p-4 text-white">
          <CurrencyDollarIcon className="h-8 w-8 mb-2 opacity-90" />
          <div className="text-sm opacity-90">Active Accounts</div>
          <div className="text-xl font-bold">{stats.active}</div>
        </div>
        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg shadow p-4 text-white">
          <CalendarIcon className="h-8 w-8 mb-2 opacity-90" />
          <div className="text-sm opacity-90">Matured</div>
          <div className="text-xl font-bold">{stats.matured}</div>
        </div>
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg shadow p-4 text-white">
          <div className="text-2xl mb-1">🏦</div>
          <div className="text-sm opacity-90">DPS</div>
          <div className="text-xl font-bold">{stats.dps}</div>
        </div>
        <div className="bg-gradient-to-r from-pink-500 to-pink-600 rounded-lg shadow p-4 text-white">
          <div className="text-2xl mb-1">💎</div>
          <div className="text-sm opacity-90">FDR</div>
          <div className="text-xl font-bold">{stats.fdr}</div>
        </div>
        <div className="bg-gradient-to-r from-teal-500 to-teal-600 rounded-lg shadow p-4 text-white">
          <div className="text-2xl mb-1">📅</div>
          <div className="text-sm opacity-90">Daily/Weekly</div>
          <div className="text-xl font-bold">{stats.daily}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 border-b">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search by account number, member name, or phone..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full px-4 py-2 pl-10 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-3" />
              </div>
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="matured">Matured</option>
              <option value="closed">Closed</option>
            </select>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Types</option>
              <option value="daily_savings">Daily Savings</option>
              <option value="weekly_savings">Weekly Savings</option>
              <option value="monthly_savings">Monthly Savings</option>
              <option value="dps">DPS</option>
              <option value="fdr">FDR</option>
            </select>
            <button
              onClick={() => refetch()}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50 flex items-center"
            >
              <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          </div>
        </div>

        {/* Accounts Table */}
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : accounts.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <BanknotesIcon className="h-12 w-12 mx-auto mb-3 text-gray-400" />
            <p>No savings accounts found</p>
            <Link to="/savings/new" className="inline-block mt-4 text-blue-600 hover:text-blue-800">
              Open your first account
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account No</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Member</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Scheme</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Interest Rate</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Start Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Maturity Date</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {accounts.map((account) => (
                  <tr key={account.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{account.account_no}</div>
                      <div className="text-xs text-gray-500">ID: {account.id}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{account.member_name}</div>
                      <div className="text-xs text-gray-500">{account.member_code}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{account.scheme_name}</div>
                      <div className="text-xs text-gray-500 capitalize">{account.scheme_type?.replace('_', ' ')}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="text-sm font-bold text-green-600">{formatCurrency(account.current_balance)}</div>
                      <div className="text-xs text-gray-500">Deposited: {formatCurrency(account.total_deposited || account.deposit_amount)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="text-sm text-gray-900">{account.interest_rate}%</div>
                      <div className="text-xs text-gray-500 capitalize">{account.interest_type || 'compound'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(account.start_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{account.maturity_date ? formatDate(account.maturity_date) : 'N/A'}</div>
                      {account.maturity_date && account.maturity_date > new Date() && (
                        <div className="text-xs text-orange-600">
                          {Math.ceil((new Date(account.maturity_date) - new Date()) / (1000 * 60 * 60 * 24))} days left
                        </div>
                      )}
                      {account.maturity_date && account.maturity_date < new Date() && (
                        <div className="text-xs text-green-600">Matured</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(account.current_status || account.status)}`}>
                        {account.current_status || account.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex justify-center space-x-2">
                        <button
                          onClick={() => handleViewDetails(account.id)}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Details"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                        {account.status === 'active' && account.current_status !== 'matured' && (
                          <>
                            <button
                              onClick={() => handleDeposit(account)}
                              className="text-green-600 hover:text-green-900"
                              title="Deposit"
                            >
                              <ArrowDownTrayIcon className="h-5 w-5" />
                            </button>
                            {account.available_for_withdrawal > 0 && (
                              <button
                                onClick={() => handleWithdraw(account)}
                                className="text-orange-600 hover:text-orange-900"
                                title="Withdraw"
                              >
                                <ArrowUpTrayIcon className="h-5 w-5" />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <td colSpan="3" className="px-6 py-4 text-right font-bold">Total Balance:</td>
                  <td className="px-6 py-4 text-right font-bold text-green-600">
                    {formatCurrency(accounts.reduce((sum, acc) => sum + (acc.current_balance || 0), 0))}
                  </td>
                  <td colSpan="5"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="px-6 py-4 border-t flex justify-between items-center">
            <div className="text-sm text-gray-500">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} entries
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={pagination.page === 1}
                className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50"
              >
                Previous
              </button>
              <span className="px-3 py-1">
                Page {pagination.page} of {pagination.total_pages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(pagination.total_pages, p + 1))}
                disabled={pagination.page === pagination.total_pages}
                className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>


      {/* WhatsApp Success Box - Shows after successful deposit */}
      {showSuccessBox && whatsappLink && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-green-800">
                ✓ Deposit successful!
              </p>
              <p className="text-sm text-green-700 mt-1">
                Share the receipt with the customer via WhatsApp:
              </p>
              <div className="mt-3">
                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.149-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.01-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                  </svg>
                  Send Receipt via WhatsApp
                </a>
              </div>
            </div>
            <div className="ml-auto pl-3">
              <button
                onClick={() => setShowSuccessBox(false)}
                className="text-green-600 hover:text-green-800"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deposit Modal */}
      {showDepositModal && selectedAccount && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-4">Deposit to Account</h2>
            <div className="mb-4">
              <p className="text-sm text-gray-600">Account: {selectedAccount.account_no}</p>
              <p className="text-sm text-gray-600">Member: {selectedAccount.member_name}</p>
              <p className="text-sm text-gray-600">Current Balance: {formatCurrency(selectedAccount.current_balance)}</p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Deposit Amount</label>
              <input
                type="number"
                value={transactionAmount}
                onChange={(e) => setTransactionAmount(e.target.value)}
                placeholder="Enter amount"
                className="w-full px-3 py-2 border rounded-lg"
                autoFocus
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDepositModal(false);
                  setTransactionAmount('');
                  setSelectedAccount(null);
                }}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (!transactionAmount || parseFloat(transactionAmount) <= 0) {
                    toast.error('Please enter a valid amount');
                    return;
                  }
                  depositMutation.mutate({
                    accountId: selectedAccount.id,
                    amount: parseFloat(transactionAmount),
                    paymentMethod: 'cash'
                  });
                }}
                disabled={depositMutation.isLoading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {depositMutation.isLoading ? 'Processing...' : 'Deposit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Withdraw Modal */}
      {showWithdrawModal && selectedAccount && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-4">Withdraw from Account</h2>
            <div className="mb-4">
              <p className="text-sm text-gray-600">Account: {selectedAccount.account_no}</p>
              <p className="text-sm text-gray-600">Member: {selectedAccount.member_name}</p>
              <p className="text-sm text-gray-600">Available Balance: {formatCurrency(selectedAccount.available_for_withdrawal || selectedAccount.current_balance)}</p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Withdrawal Amount</label>
              <input
                type="number"
                value={transactionAmount}
                onChange={(e) => setTransactionAmount(e.target.value)}
                placeholder="Enter amount"
                className="w-full px-3 py-2 border rounded-lg"
                autoFocus
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Reason for Withdrawal</label>
              <textarea
                value={withdrawReason}
                onChange={(e) => setWithdrawReason(e.target.value)}
                rows="3"
                placeholder="Please provide a reason..."
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowWithdrawModal(false);
                  setTransactionAmount('');
                  setWithdrawReason('');
                  setSelectedAccount(null);
                }}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (!transactionAmount || parseFloat(transactionAmount) <= 0) {
                    toast.error('Please enter a valid amount');
                    return;
                  }
                  if (!withdrawReason) {
                    toast.error('Please provide a reason for withdrawal');
                    return;
                  }
                  withdrawMutation.mutate({
                    accountId: selectedAccount.id,
                    amount: parseFloat(transactionAmount),
                    reason: withdrawReason,
                    paymentMethod: 'cash'
                  });
                }}
                disabled={withdrawMutation.isLoading}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
              >
                {withdrawMutation.isLoading ? 'Processing...' : 'Withdraw'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SavingsAccountList;